export interface MediaDescription {
  url: string;
  description: string;
}

export type MediaDescriptions = Record<string, string>;

export function mediaDescriptionKey(value: string): string {
  try {
    const url = new URL(value);
    if (!["http:", "https:"].includes(url.protocol)) return value;
    const variants =
      (url.hostname.startsWith("mediaserver.") &&
        url.hostname.endsWith(".realequity.dk")) ||
      url.hostname === "imagedelivery.net";
    let path = decodeURIComponent(url.pathname).replace(/\/$/, "");
    if (variants) path = path.replace(/\/(?:xs|sm|md|lg|xl|original)$/, "");
    return url.host.toLowerCase() + path + (variants ? "" : url.search);
  } catch {
    return value;
  }
}

export function mediaDescriptionMap(
  items: readonly MediaDescription[],
): MediaDescriptions {
  const result: MediaDescriptions = {};
  for (const item of items)
    if (item.description.trim())
      result[mediaDescriptionKey(item.url)] = item.description.trim();
  return result;
}

export function resolveMediaAlt(
  descriptions: MediaDescriptions,
  url: string,
  fallback: string,
  legacyAlt?: string,
  decorative = false,
): string {
  if (decorative) return "";
  return (
    legacyAlt?.trim() || descriptions[mediaDescriptionKey(url)] || fallback
  );
}

export function collectMediaDescriptionUrls(data: unknown): string[] {
  const urls = new Set<string>();
  const add = (value: string) => {
    const decoded = value.replaceAll("&amp;", "&");
    if (
      /^https?:\/\//i.test(decoded) &&
      (/\/images\//i.test(decoded) ||
        /imagedelivery\.net\//i.test(decoded) ||
        /\.(?:jpe?g|png|gif|webp|svg|avif|ico)(?:[?#]|$)/i.test(decoded))
    )
      urls.add(decoded);
  };
  const visit = (value: unknown) => {
    if (typeof value === "string") {
      add(value);
      for (const match of value.matchAll(
        /<img\b[^>]*\ssrc\s*=\s*["']([^"']+)["']/gi,
      ))
        add(match[1]);
    } else if (Array.isArray(value)) value.forEach(visit);
    else if (value && typeof value === "object")
      Object.values(value).forEach(visit);
  };
  visit(data);
  return [...urls].sort();
}

export function richTextMediaDescriptions(
  html: string,
  descriptions: MediaDescriptions,
): string {
  return html.replace(/<img\b(?:[^"'>]|"[^"]*"|'[^']*')*>/gi, (tag) => {
    const src = /\ssrc\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(tag);
    if (!src) return tag;
    const previous = /\salt\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(tag);
    if ((previous?.[1] ?? previous?.[2] ?? previous?.[3] ?? "").trim())
      return tag;
    if (
      /\s(?:role\s*=\s*["']presentation["']|aria-hidden\s*=\s*["']true["'])/i.test(
        tag,
      )
    )
      return tag;
    const text =
      descriptions[
        mediaDescriptionKey((src[1] ?? src[2]).replaceAll("&amp;", "&"))
      ];
    if (!text) return tag;
    const attribute = `alt="${text.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")}"`;
    return previous
      ? tag.replace(previous[0], ` ${attribute}`)
      : tag.replace(/<img\b/i, `<img ${attribute}`);
  });
}

export function applyRichTextMediaDescriptions<T>(
  data: T,
  descriptions: MediaDescriptions,
): T {
  const visit = (
    value: unknown,
    specialCode = false,
    blog = false,
  ): unknown => {
    if (specialCode) return value;
    if (typeof value === "string")
      return /<img\b/i.test(value)
        ? richTextMediaDescriptions(value, descriptions)
        : value;
    if (Array.isArray(value))
      return value.map((item) => visit(item, false, blog));
    if (!value || typeof value !== "object") return value;
    const record = value as Record<string, unknown>;
    if (
      typeof record.type === "string" &&
      /CustomHtml|HtmlSection|HtmlComponent|Script/i.test(record.type)
    )
      return value;
    const result = Object.fromEntries(
      Object.entries(record).map(([key, item]) => [
        key,
        visit(
          item,
          /^(?:compiled|scripts?|css|code|javascript)$/i.test(key),
          blog || key === "blog",
        ),
      ]),
    );
    if (
      blog &&
      typeof record.url === "string" &&
      ("altText" in record || descriptions[mediaDescriptionKey(record.url)])
    )
      result.altText = descriptions[mediaDescriptionKey(record.url)] ?? "";
    return result;
  };
  return visit(data) as T;
}
